import { useQBankStructure } from '@exam-management/qbs-provider';
import {
  Button,
  DialogActions,
  Divider,
  Flex,
  Form,
  Text,
  ToggleButton,
  ToggleButtonGroup,
} from '@shared/components';
import { zodResolver } from '@shared/utils';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { QuestionDuplicationCustomLocation } from '../question-duplication-custom-location';

import {
  getQuestionDuplicateSchema,
  type QuestionDuplicationFormInput,
} from './question-duplication-form-schema';

enum DuplicateMethodValue {
  QUICK_DUPLICATE = 'QUICK_DUPLICATE',
  CUSTOM_LOCATION = 'CUSTOM_LOCATION',
}

interface QuestionDuplicationFormProps {
  onSubmit: (data?: QuestionDuplicationFormInput) => void;
  onClose: () => void;
  qId: string;
  qContentId: string;
  loading: boolean;
}

export default function QuestionDuplicationForm(props: Readonly<QuestionDuplicationFormProps>) {
  const { onSubmit, onClose, qId, loading: isDuplicatingQuestion } = props;
  const { qBankStructureLevels } = useQBankStructure();
  const QuestionDuplicationFormSchema = getQuestionDuplicateSchema(qBankStructureLevels);

  const formContext = useForm<QuestionDuplicationFormInput>({
    resolver: zodResolver(QuestionDuplicationFormSchema),
  });

  const [duplicateMethod, setDuplicateMethod] = useState<DuplicateMethodValue>(
    DuplicateMethodValue.QUICK_DUPLICATE,
  );

  const handleDuplicateMethod = (
    _: React.MouseEvent<HTMLElement>,
    newDuplicateMethod: DuplicateMethodValue,
  ) => {
    if (newDuplicateMethod === null) {
      return;
    }
    setDuplicateMethod(newDuplicateMethod);
  };

  return (
    <>
      <Flex
        sx={(theme) => ({
          flexDirection: 'column',
          gap: theme.spacing(4),
          p: theme.spacing(4, 6),
        })}
      >
        <Flex
          sx={(theme) => ({
            borderRadius: theme.shape.rounded(2),
            border: `1px solid ${theme.palette.grey[200]}`,
            bgcolor: theme.palette.grey[50],
            p: theme.spacing(3, 4),
            gap: theme.spacing(2),
          })}
        >
          <Text variant='body2'>QID:</Text>
          <Text variant='body2Semibold'>{qId}</Text>
        </Flex>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(2),
          })}
        >
          <Text>Select Duplication Method</Text>
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(4),
              p: theme.spacing(4),
              borderRadius: theme.shape.rounded(2),
              border: `1px solid ${theme.palette.grey[300]}`,
            })}
          >
            <ToggleButtonGroup
              value={duplicateMethod}
              onChange={handleDuplicateMethod}
              aria-label='Question duplication methods'
              exclusive
              fullWidth
            >
              <ToggleButton
                value={DuplicateMethodValue.QUICK_DUPLICATE}
                sx={(theme) => ({
                  p: theme.spacing(2),
                })}
              >
                Quick Duplicate
              </ToggleButton>
              <ToggleButton
                value={DuplicateMethodValue.CUSTOM_LOCATION}
                sx={(theme) => ({
                  p: theme.spacing(2),
                })}
              >
                Custom Location
              </ToggleButton>
            </ToggleButtonGroup>
            {duplicateMethod === DuplicateMethodValue.QUICK_DUPLICATE ? (
              <Text>Duplicate Question to the Same Location with a new QID</Text>
            ) : null}
            {duplicateMethod === DuplicateMethodValue.CUSTOM_LOCATION ? (
              <FormProvider {...formContext}>
                <Form>
                  <QuestionDuplicationCustomLocation />
                </Form>
              </FormProvider>
            ) : null}
          </Flex>
        </Flex>
      </Flex>
      <Divider />
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={onClose} size='large'>
          Cancel
        </Button>
        <Button
          size='large'
          onClick={async () => {
            if (duplicateMethod === DuplicateMethodValue.QUICK_DUPLICATE) {
              onSubmit();
            }
            formContext.handleSubmit(onSubmit)();
            formContext.reset();
          }}
          loading={isDuplicatingQuestion}
        >
          Duplicate
        </Button>
      </DialogActions>
    </>
  );
}
